import { z } from 'zod';
import { validStringCharacterMessage, validStringCharacters } from './shared-validators/regex.ts';

export const editorTypeSchema = z.enum(['DEFAULT', 'TEXT', 'TEXT_AND_DATE', 'RADIOS', 'LIST', 'PICK_LIST', 'DATE']);
export type EditorType = z.infer<typeof editorTypeSchema>;

const partsTypeSchema = z.enum(['TEXT', 'VARIABLE']);
export type PartsType = z.infer<typeof partsTypeSchema>;

export const ruleEditorControlSchema = z.object({
  Type: z.enum(['TEXT', 'DATE', 'RADIOS', 'LIST', 'PICK_LIST', 'VACCINE_TARGET', 'LABEL']),
  Label: z.string(),
  SecondaryLabel: z.string().optional(),
  PlaceholderText: z.string().optional(),
  Values: z.array(z.object({
    Value: z.string(),
    Label: z.string(),
  })).optional(),
  DefaultValue: z.string().optional(),
  HelperText: z.string().optional(),
  Width: z.number().optional(),
  Validation: z.object({
    RegEx: z.string(),
    ErrorMessage: z.string(),
  }).optional(),
});

export type RuleEditorControl = z.infer<typeof ruleEditorControlSchema>;

export const ruleEditorSchema = z.object({
  ID: z.string().optional(),
  Type: editorTypeSchema,
  NameTemplate: z.array(z.object({
    Type: partsTypeSchema,
    Value: z.string().optional(),
  })).optional(),
  DescriptionTemplate: z.array(z.object({
    Type: partsTypeSchema,
    Value: z.string().optional(),
  })).optional(),
  ComparatorTemplate: z.array(z.object({
    Type: partsTypeSchema,
    Value: z.string().optional(),
  })).optional(),
  MetaData: z.object({
    Controls: z.array(ruleEditorControlSchema).optional(),
  }).optional(),
});

export type RuleEditor = z.infer<typeof ruleEditorSchema>;

const restrictedString = z
  .string()
  .regex(validStringCharacters, validStringCharacterMessage);

export const ruleTypeMap = {
  F: 'Filter',
  S: 'Suppression',
  R: 'Routing',
} as const;

export type RuleType = keyof typeof ruleTypeMap;
const ruleTypeKeys = Object.keys(ruleTypeMap) as RuleType[];

export const ruleTypeSchema = z.enum(ruleTypeKeys as [RuleType, ...RuleType[]]);

export const iterationRuleSchema = z.object({
  ID: z.string().optional(),
  Type: ruleTypeSchema.optional(),
  CohortLabel: z.string().optional(),
  Name: restrictedString.optional(),
  Description: restrictedString.optional(),
  Priority: z.number().optional(),
  Operator: z.string().optional(),
  Comparator: z.string().optional(),
  AttributeTarget: z.string().optional(),
  AttributeLevel: z.string().optional(),
  AttributeName: z.string().optional(),
  /** CommsRouting is only relevant for Routing rules. */
  CommsRouting: z.string().optional(),
  AndNumber: z.number().optional(),
  LibraryRuleMetadata: ruleEditorSchema.optional(),
});

export type IterationRule = z.infer<typeof iterationRuleSchema>;

export default iterationRuleSchema;
